<template>
  <ag-grid-vue
    :row-data="items"
    :column-defs="columns"
    :get-row-id="getRowId"
    :pagination="gridPagination"
    :pagination-page-size="gridPageSize"
    :pagination-page-size-selector="gridPageSizeOptions"
    :grid-options="{
      tooltipShowMode: 'whenTruncated',
      suppressDragLeaveHidesColumns: true,
      autoSizeStrategy: {
        type: 'fitCellContents',
        colIds: columns.map((col) => col.field||''),
      },
      singleClickEdit: true,
      readOnlyEdit: true,
      stopEditingWhenCellsLoseFocus: true,
      tooltipShowDelay: 500,
      tooltipInteraction: true,
      theme: myTheme,
      onGridSizeChanged: onGridSizeChanged,
      onFirstDataRendered: onGridSizeChanged,
      onGridColumnsChanged: onGridSizeChanged,
    }"
    :style="`height: 100%; max-width: 100%; min-width: fit-content;` + (gridWidth ? `width: ${gridWidth}px;` : '')"
    @sort-changed="refreshCells"
    @filter-changed="refreshCells"
    @grid-ready="onGridReady"
    @cell-edit-request="$emit('cellEditRequest', $event)"
  />
</template>

<script lang="ts">
import {AgGridVue} from 'ag-grid-vue3';
import {defineComponent, PropType} from 'vue';
import type {ColDef, GetRowIdFunc} from './types';
import DeletableColumnHeader from './DeletableColumnHeader.vue';
import {
  GridApi,
  GridReadyEvent,
  ModuleRegistry,
  ValidationModule,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  PaginationModule,
  CellStyleModule,
  TextFilterModule,
  TooltipModule,
  NumberFilterModule,
  TextEditorModule,
  themeQuartz,
  GridSizeChangedEvent,
  FirstDataRenderedEvent,
  ColumnApiModule,
  GridColumnsChangedEvent,
  RenderApiModule,
  EventApiModule,
} from 'ag-grid-community';

if (process.env.NODE_ENV !== 'production') {
  // Having the ValidationModule in development mode helps catch issues with the grid configuration and unregistered
  // modules. It is not recommended to include it in production builds.
  ModuleRegistry.registerModules([ValidationModule]);
}
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  PaginationModule,
  CellStyleModule,
  TextFilterModule,
  TooltipModule,
  NumberFilterModule,
  TextEditorModule,
  ColumnApiModule,
  RenderApiModule,
  EventApiModule,
]);

export default defineComponent({
  name: 'TableView',
  components: {
    AgGridVue,
    // This component must be registered for AG Grid to use it as a custom header, even though it is not referenced
    // directly.
    // eslint-disable-next-line vue/no-unused-components
    DeletableColumnHeader,
  },
  props: {
    items: {
      type: Array as PropType<Record<string, unknown>[]>,
      required: true,
    },
    columns: {
      type: Array as PropType<(ColDef)[]>,
      required: true,
    },
    getRowId: {
      type: Function as PropType<GetRowIdFunc>,
      default: undefined,
    },
  },
  emits: ['cellEditRequest'],
  data() {
    const defaultBorderStyle = {style: 'solid', color: 'rgba(0, 0, 0, 0.12)', width: '1px'};
    return {
      gridPagination: true as boolean,
      gridPageSize: 25 as number,
      gridPageSizeOptions: [5, 10, 25, 50, 100] as number[],
      gridApi: null as GridApi<Record<string, unknown>> | null,
      myTheme: themeQuartz.withParams({
        wrapperBorderRadius: '0',
        rowBorder: defaultBorderStyle,
        columnBorder: defaultBorderStyle,
        headerColumnBorder: defaultBorderStyle,
        spacing: '0.25rem',
      }),
      gridWidth: 0 as number,
    };
  },
  methods: {
    refreshCells(event: {api: {refreshCells: () => void}}) {
      // The table sometimes becomes blank if the refresh is done immediately
      setTimeout(() => {
        // This is to keep the row numbers in sync with the grid
        event.api.refreshCells();
      }, 0);
    },
    /** Callback for when the grid is ready */
    onGridReady(evt: GridReadyEvent) {
      this.gridApi = evt.api;
    },
    /** Callback for when the grid size changes */
    onGridSizeChanged(params: GridSizeChangedEvent | FirstDataRenderedEvent | GridColumnsChangedEvent) {
      const totalWidth = params.api.getColumns()?.reduce((acc, col) => acc + (col.getActualWidth() || 0), 0) || 800;
      this.gridWidth = totalWidth + 1; // the actual grid width is slightly larger to accommodate borders
    },
  },
});
</script>

